import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  PageHead,
  Panel,
  PersonCell,
  Pill,
  Skeleton,
  Timeline,
  useToast,
} from "@guestpilot/ui-kit";
import { ArrowLeft16Regular, ArrowSync16Regular } from "@fluentui/react-icons";
import { useGet, useGetPages, useWrite } from "../api/hooks";
import { ApiProblemError } from "../api/client";
import type { PersonRef, Schema, Team } from "../api/types";
import { PersonPicker } from "../components/PersonPicker";
import {
  ParticipantDrawer,
  ParticipantsTable,
  RemovalDialog,
  type Participant,
} from "../components/Participants";
import { RISK_ORDER } from "../components/TeamsTable";
import { useHandleApiError } from "../errors";
import { formatDate, formatDateTime } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Campaign = Schema<"Campaign">;
type Audit = Schema<"AuditEvent">;

/** Everything about one Team for the admin: risks, every external user, summary and recent history (personal-app.md Team Detail). */
export function TeamDetail() {
  const { teamId = "" } = useParams();
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const fail = useHandleApiError();
  const tz = s.me.tenant.timeZone;
  const team = useGet<Team>(`/api/v1/teams/${teamId}`);
  const [risk, setRisk] = useState<string | null>(null);
  const users = useGetPages<Participant>("/api/v1/participants", {
    teamId,
    risk: risk ?? undefined,
    limit: 50,
  });
  const history = useGet<{ items: Audit[] }>(
    `/api/v1/teams/${teamId}/history`,
    { limit: 4 },
    { enabled: s.can("AUDIT_READ", teamId) },
  );
  const campaign = useGet<Campaign>(`/api/v1/campaigns/${team.data?.activeCampaignId ?? "none"}`, undefined, {
    enabled: Boolean(team.data?.activeCampaignId),
  });
  const scan = useWrite("POST", "/api/v1/teams/:id/scans", ["/api/v1/teams", "/api/v1/dashboard"]);
  const [edit, setEdit] = useState<Participant | null>(null);
  const [remove, setRemove] = useState<Participant | null>(null);
  const [deactivate, setDeactivate] = useState(false);
  const [delegate, setDelegate] = useState(false);

  if (team.isLoading) return <Skeleton rows={6} />;
  if (!team.data) return <EmptyState title={t("errors:not_found")} />;
  const d = team.data;
  const chips = RISK_ORDER.filter((r) => (d.riskCounts?.[r] ?? 0) > 0);

  return (
    <div className="gp-page">
      <Link to="/teams" className="t-body-strong">
        <ArrowLeft16Regular aria-hidden /> {t("ui:teamDetail.back")}
      </Link>
      <PageHead
        title={d.displayName}
        subtitle={t("ui:teamDetail.sub", {
          n: d.externalUserCount ?? 0,
          date: formatDateTime(d.lastScanAt, locale, tz),
          owner: (d.owners ?? []).map((o) => o.displayName).join(", ") || t("ui:teams.ownerless"),
        })}
        actions={
          <>
            <Button
              disabledReason={s.gate("TEAM_SCAN", teamId)}
              onClick={() =>
                scan.mutate(
                  { params: { id: teamId } },
                  {
                    onSuccess: () => toast.success(t("ui:teamDetail.scanQueued")),
                    onError: (e) =>
                      e instanceof ApiProblemError && (e.status === 409 || e.status === 422)
                        ? toast.error(t("ui:teamDetail.scanWait"))
                        : fail(e),
                  },
                )
              }
            >
              <ArrowSync16Regular aria-hidden /> {t("ui:teamDetail.refresh")}
            </Button>
            <Button onClick={() => nav(`/exports?team=${teamId}`)}>{t("ui:teamDetail.export")}</Button>
            <Button
              variant="primary"
              disabledReason={s.gate("CAMPAIGN_LAUNCH")}
              onClick={() => nav(`/campaigns?new=1&team=${teamId}`)}
            >
              {t("ui:teamDetail.launch")}
            </Button>
          </>
        }
      />
      {d.status === "team_unavailable" ? (
        <Banner tone="danger">{t("ui:teamDetail.unavailable")}</Banner>
      ) : null}
      {d.status === "blocked_limit" ? (
        <Banner
          tone="warn"
          action={
            s.can("SUBSCRIPTION_READ") ? (
              <Button onClick={() => nav("/subscription")}>{t("ui:activate.upgrade")}</Button>
            ) : undefined
          }
        >
          {t("ui:activate.limit")}
        </Banner>
      ) : null}
      {chips.length > 0 ? (
        <div className="gp-row" role="group" aria-label={t("ui:home.filterRisk")}>
          {chips.map((r) => (
            <Chip
              key={r}
              variant={
                r === "expired" || r === "overdue_review" || r === "unknown_domain" ? "danger" : "warn"
              }
              pressed={risk === r}
              onClick={() => setRisk(risk === r ? null : r)}
            >
              {t(`ui:risk.${r}`)} {d.riskCounts?.[r]}
            </Chip>
          ))}
        </div>
      ) : null}
      <div className="gp-grid-detail">
        <Panel label={t("ui:users.title")}>
          <ParticipantsTable
            rows={users.items}
            loading={users.isLoading}
            error={users.isError}
            onRetry={() => void users.refetch()}
            onEdit={setEdit}
            onRemove={setRemove}
          />
          {users.hasNextPage ? (
            <div className="gp-actions" style={{ padding: "var(--space-12)", justifyContent: "center" }}>
              <Button onClick={() => void users.fetchNextPage()}>{t("ui:users.loadMore")}</Button>
            </div>
          ) : null}
        </Panel>
        <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
          <Card title={t("ui:teamDetail.summary")}>
            <dl className="gp-kv t-body">
              <dt>{t("ui:teamDetail.owner")}</dt>
              <dd>
                {(d.owners ?? []).map((o) => o.displayName).join(", ") || (
                  <Pill variant="warn">{t("ui:teams.ownerless")}</Pill>
                )}
              </dd>
              <dt>{t("ui:teamDetail.delegates")}</dt>
              <dd>{(d.delegates ?? []).map((o) => o.displayName).join(", ") || "—"}</dd>
              <dt>{t("ui:teamDetail.activated")}</dt>
              <dd>{formatDate(d.activatedAt, locale, tz)}</dd>
              <dt>{t("ui:teamDetail.channels")}</dt>
              <dd>
                {t("ui:teamDetail.channelsValue", {
                  n: d.channelsScanned ?? 0,
                  p: d.privateChannelsScanned ?? 0,
                })}
              </dd>
              <dt>{t("ui:teamDetail.campaign")}</dt>
              <dd>
                {campaign.data
                  ? `${campaign.data.name} · ${t(`ui:campaignStatus.${campaign.data.status}`)}`
                  : "—"}
              </dd>
            </dl>
            <div className="gp-actions">
              <Button
                variant="ghost"
                disabledReason={s.gate("DELEGATE_MANAGE", teamId)}
                onClick={() => setDelegate(true)}
              >
                {t("ui:teamDetail.addDelegate")}
              </Button>
              {d.status === "active" ? (
                <Button
                  variant="danger"
                  disabledReason={s.gate("TEAM_DEACTIVATE", teamId)}
                  onClick={() => setDeactivate(true)}
                >
                  {t("ui:teamDetail.deactivate")}
                </Button>
              ) : null}
            </div>
          </Card>
          {s.can("AUDIT_READ", teamId) ? (
            <Card title={t("ui:teamDetail.history")}>
              {history.isLoading ? (
                <Skeleton rows={3} />
              ) : (
                <Timeline
                  events={(history.data?.items ?? []).map((e) => ({
                    id: e.id ?? "",
                    summary:
                      t(`ui:audit.${e.action}`) === `ui:audit.${e.action}`
                        ? (e.action ?? "")
                        : t(`ui:audit.${e.action}`),
                    meta: e.actor?.displayName,
                    at: e.occurredAt ?? "",
                    atLabel: formatDateTime(e.occurredAt, locale, tz),
                  }))}
                />
              )}
            </Card>
          ) : null}
        </div>
      </div>
      <ParticipantDrawer participant={edit} onClose={() => setEdit(null)} />
      <RemovalDialog participant={remove} onClose={() => setRemove(null)} />
      <DeactivateDialog open={deactivate} team={d} onClose={() => setDeactivate(false)} />
      <DelegateDialog open={delegate} teamId={teamId} onClose={() => setDelegate(false)} />
    </div>
  );
}

function DeactivateDialog({ open, team, onClose }: { open: boolean; team: Team; onClose: () => void }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const nav = useNavigate();
  const m = useWrite("POST", "/api/v1/teams/:id/deactivate", [
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/subscription",
  ]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("ui:deactivate.title", { name: team.displayName })}
      description={t("ui:deactivate.lead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="danger"
            onClick={() =>
              m.mutate(
                { params: { id: team.id } },
                {
                  onSuccess: () => {
                    onClose();
                    nav("/teams");
                  },
                  onError: fail,
                },
              )
            }
          >
            {t("ui:deactivate.confirm")}
          </Button>
        </>
      }
    >
      <Banner tone="warn">{t("ui:deactivate.keeps")}</Banner>
    </Dialog>
  );
}

function DelegateDialog({ open, teamId, onClose }: { open: boolean; teamId: string; onClose: () => void }) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const add = useWrite<PersonRef, { personId: string }>("POST", "/api/v1/teams/:id/delegates", [
    "/api/v1/teams",
  ]);
  const [person, setPerson] = useState<PersonRef | null>(null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("ui:delegate.title")}
      description={t("ui:delegate.lead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="primary"
            disabledReason={person ? undefined : t("ui:delegate.pick")}
            onClick={() =>
              add.mutate(
                { params: { id: teamId }, body: { personId: person!.id } },
                {
                  onSuccess: () => {
                    setPerson(null);
                    onClose();
                  },
                  onError: fail,
                },
              )
            }
          >
            {t("ui:delegate.add")}
          </Button>
        </>
      }
    >
      <PersonPicker label={t("ui:delegate.title")} value={person} onChange={setPerson} />
      {person ? <PersonCell name={person.displayName} sub={person.email} /> : null}
    </Dialog>
  );
}
